import { InstancedMesh } from 'three';

import Background from '../Materials/Background/Background.js';
import Cloth from '../Materials/Cloth/Cloth.js'
import Eye from '../Materials/Eye/Eye.js'
import Lashes from '../Materials/Lashes/Lashes.js'
import Lights from '../Materials/Lights/Lights.js'
import MouseComputation from '../Materials/MouseComputation/MouseComputation.js'
import PBR from '../Materials/PBR/PBR.js'
import Screen from '../Materials/Screen/Screen.js'
import ScreenGlow from '../Materials/ScreenGlow/ScreenGlow.js'
import Skin from '../Materials/Skin/Skin.js'

import { disposeMesh } from './misc.js';

// import config from "/src/Lisa/jsons/configs/default.json";

const MATERIAL_CHARACTERS_TO_IGNORE = [' ', '-', /[0-9]/g, '.'];
const MATERIALS_TO_REPLACE = {};
const DEFAULT_MATERIAL = 'PBR';
// const MATERIALS = config.material;

const materialClasses = Object.fromEntries(
	Object.entries(
		{
            Background,
            Cloth,
            Eye,
            Lashes,
            Lights,
            MouseComputation,
            PBR,
            Screen,
            ScreenGlow,
            Skin
        }
	).map(([key, value]) => {
		const name = key.split('/').pop().split('.')[0];
		return [name, value];
	}),
);

function applyModifier(child, userData) {
	switch (userData[0]) {
		case 'blending':
			child.blending = userData[1];
			child.depthWrite = false;
			// child.depthTest = false;
			break;
		case 'visible':
			child.visible = userData[1] !== 'false';
			break;
		case 'renderOrder':
			child.renderOrder = ~~userData[1];
			break;
	}
}

const cachedMaterials = new Map();
function applyMaterial(child, configurationPrefix = '') {
	if (configurationPrefix) configurationPrefix += '-';

	if (
		(!child.material.isMeshStandardMaterial && !child.material.isMeshPhysicalMaterial) ||
		child.material.userData?.keepStandard
	)
		return child.material;

	let name = child.material.name;
	const uniqName = configurationPrefix + name;

	MATERIAL_CHARACTERS_TO_IGNORE.forEach((char) => {
		name = name.replaceAll(char, '');
	});

	if (MATERIALS_TO_REPLACE[name]) name = MATERIALS_TO_REPLACE[name];

	const materialClass = materialClasses[name] || materialClasses[DEFAULT_MATERIAL];

	Object.assign(child.material, {
		// ...MATERIALS?.[name],
		// ...MATERIALS?.[uniqName],
		name: uniqName,
	});

	const uniqId = uniqName + uniqIdMaterial(child);
	if (!cachedMaterials.has(uniqId)) cachedMaterials.set(uniqId, new materialClass(child.material, child));
	child.material = cachedMaterials.get(uniqId);

	return child.material;
}

export function applyInstances(object, ignoreList = []) {
	const instances = {};
	let name;

	object.traverse((child) => {
		name = child.name.replace('.', '');
		instances[name] = [child];

		if (child.geometry && !child.instanceApplied) {
			object.traverse((child2) => {
				if (
					child.name === child2.name ||
					!child2.geometry ||
					child2.instanceApplied ||
					child.parent !== child2.parent
				)
					return;
				if (child.geometry.uuid === child2.geometry.uuid) {
					child2.instanceApplied = true;
					instances[name].push(child2);
				}
			});
		}
		if (instances[name].length <= 1) delete instances[name];
	});

	for (const i in instances) {
		const instanceOf = object.getObjectByName(i);
		if (typeof instanceOf !== 'undefined') {
			if (ignoreList.indexOf(instanceOf.name) >= 0) {
				return;
			}
			instanciateMesh(instanceOf, instances[i], instanceOf.parent);
		}
	}
}

export function applyModifiers(object, configurationPrefix = '') {
	object?.traverse((child) => {
		if (child.material?.name) applyMaterial(child, configurationPrefix);

		if (child.material?.userData && Object.entries(child.material?.userData).length > 0) {
			Object.entries(child.material.userData).forEach((userData) => {
				applyModifier(child.material, userData);
			});
		}
		if (child.userData && Object.entries(child.userData).length > 0) {
			Object.entries(child.userData).forEach((userData) => applyModifier(child, userData));
		}
	});
}

export function instanciateMesh(reference, meshes, parent) {
	// console.log('instanciate', reference.material.uuid, reference.name, meshes);

	const instances = new InstancedMesh(reference.geometry, reference.material, meshes.length);
	instances.material.userData = reference.material?.userData;
	instances.userData = reference.userData;
	instances.name = 'instance-' + reference.name;
	// instances.frustumCulled = true;

	meshes.forEach((child, i) => {
		child.updateMatrixWorld();
		instances.setMatrixAt(i, child.matrixWorld);

		disposeMesh(child);
		child.removeFromParent();
	});
	// instances.instanceMatrix.needsUpdate = true;

	// console.log(instances);

	parent.add(instances);
	return instances;
}

function uniqIdMaterial(caller) {
	return (
		!!caller.instanceMatrix +
		caller.isSkinnedMesh +
		(caller.material?.roughnessMap?.uuid || '') +
		(caller.material?.aoMap?.uuid || '') +
		(caller.material?.map?.uuid || '') +
		(caller.material?.metalnessMap?.uuid || '') +
		(caller.material?.normalMap?.uuid || '') +
		(caller.userData.ao?.uuid || '')
	);
}
